import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../config/firebase';

// ═══════════════════════════════════════════════════════════════
// ADMIN MFA SETUP (Batch 16 M3)
// ═══════════════════════════════════════════════════════════════

export default function AdminMfaSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState('loading'); // loading | status | enroll | verify | backup | done
  const [mfaStatus, setMfaStatus] = useState(null);
  const [secret, setSecret] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const loadStatus = async () => {
    try {
      const callable = httpsCallable(functions, 'getMfaStatus');
      const result = await callable();
      setMfaStatus(result.data);
      setStep('status');
    } catch (e) {
      setError(e.message);
      setStep('status');
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const startEnrollment = async () => {
    setError('');
    setLoading(true);
    try {
      const callable = httpsCallable(functions, 'generateMfaSecret');
      const result = await callable();
      setSecret(result.data.secret);
      setOtpauthUrl(result.data.otpauthUrl);
      setStep('enroll');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmEnrollment = async () => {
    setError('');
    setLoading(true);
    try {
      const callable = httpsCallable(functions, 'confirmMfaEnrollment');
      const result = await callable({ token: verificationCode });
      setBackupCodes(result.data.backupCodes || []);
      setStep('backup');
      // Refresh auth token so MFA claim updates
      await auth.currentUser.getIdToken(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    const code = prompt('Enter your current 6-digit MFA code to disable MFA:');
    if (!code) return;
    setLoading(true);
    try {
      const callable = httpsCallable(functions, 'disableMfa');
      await callable({ token: code });
      await auth.currentUser.getIdToken(true);
      await loadStatus();
    } catch (e) {
      alert(`Failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // QR code via Google Chart API (works without external deps)
  const qrUrl = otpauthUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthUrl)}`
    : null;

  if (step === 'loading') {
    return <div className="p-10 text-center">Loading...</div>;
  }

  return (
    <div className="max-w-[600px] mx-auto p-6">
      <button onClick={() => navigate(-1)}
        className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0 mb-4">
        ← Back
      </button>

      <h1 className="font-serif mt-0 mb-2">Two-Factor Authentication</h1>
      <p className="text-body text-[0.9rem] mb-5">
        Strengthen your admin account with an authenticator app (Google Authenticator, Authy, 1Password, etc.).
      </p>

      {/* Status view */}
      {step === 'status' && (
        <div className="p-5 rounded-xl bg-white border border-slate-200">
          {mfaStatus?.enrolled ? (
            <>
              <div className="p-3 rounded-lg font-semibold mb-3.5 text-emerald-600" style={{ background: 'rgba(45,212,168,0.1)' }}>
                ✓ MFA enabled
              </div>
              <div className="text-[0.88rem] text-body mb-1.5">
                Enrolled: {new Date(mfaStatus.enrolledAt).toLocaleDateString('en-AU')}
              </div>
              <div className="text-[0.88rem] text-body mb-1.5">
                Last used: {mfaStatus.lastUsedAt ? new Date(mfaStatus.lastUsedAt).toLocaleString('en-AU') : 'Never'}
              </div>
              <div className="text-[0.88rem] text-body mb-4">
                Backup codes remaining: <strong>{mfaStatus.remainingBackupCodes}</strong>
              </div>
              <button onClick={handleDisable} disabled={loading}
                className="px-3.5 py-2 rounded-lg border-[1.5px] border-red-600 bg-white text-red-600 cursor-pointer font-semibold text-[0.88rem]">
                Disable MFA
              </button>
            </>
          ) : (
            <>
              <div className="p-3 rounded-lg bg-red-100 text-red-900 font-semibold mb-3.5">
                ⚠ MFA not enabled
              </div>
              <p className="text-[0.88rem] text-body mb-4">
                MFA will be <strong>required</strong> for admin access after {mfaStatus?.pinDeprecationDate}.
                Enrol now to avoid being locked out.
              </p>
              <button onClick={startEnrollment} disabled={loading}
                className="px-4 py-2.5 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.92rem]">
                {loading ? 'Starting...' : 'Set up MFA now'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Enrollment QR */}
      {step === 'enroll' && (
        <div className="p-5 rounded-xl bg-white border border-slate-200">
          <h3 className="mt-0 mb-3">Step 1 of 2: Scan QR code</h3>
          <p className="text-[0.88rem] text-body mb-3.5">
            Open your authenticator app and scan this QR code. Or enter the secret manually:
          </p>
          <div className="text-center mb-3.5">
            {qrUrl && <img src={qrUrl} alt="MFA QR" className="w-[200px] h-[200px] inline-block" />}
          </div>
          <div className="p-2.5 rounded-lg bg-slate-50 font-mono text-[0.85rem] text-center break-all mb-[18px]">
            {secret}
          </div>
          <h3 className="mt-0 mb-3">Step 2 of 2: Verify</h3>
          <p className="text-[0.88rem] text-body mb-2">
            Enter the 6-digit code shown in your authenticator app:
          </p>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            className="w-full p-3 rounded-lg border border-slate-200 text-[1.2rem] text-center tracking-[4px] mb-3"
          />
          {error && <div className="text-red-600 text-[0.85rem] mb-2.5">{error}</div>}
          <button onClick={confirmEnrollment} disabled={loading || verificationCode.length !== 6}
            className="w-full px-4 py-2.5 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.92rem]"
            style={{ opacity: verificationCode.length === 6 ? 1 : 0.5 }}>
            {loading ? 'Verifying...' : 'Verify & enable MFA'}
          </button>
        </div>
      )}

      {/* Backup codes display */}
      {step === 'backup' && (
        <div className="p-5 rounded-xl bg-white border border-slate-200">
          <div className="p-3 rounded-lg font-semibold mb-3.5 text-emerald-600" style={{ background: 'rgba(45,212,168,0.1)' }}>
            ✓ MFA enabled successfully
          </div>
          <h3 className="mt-0 mb-2">Save your backup codes</h3>
          <p className="text-[0.88rem] text-body mb-3.5">
            Print or store these codes somewhere safe. Each code can be used <strong>once</strong> if you lose access
            to your authenticator app. You won't see these codes again.
          </p>
          <div className="p-4 rounded-lg bg-slate-50 mb-3.5 font-mono text-[0.95rem] grid grid-cols-2 gap-2">
            {backupCodes.map((code, i) => (
              <div key={i}>{code}</div>
            ))}
          </div>
          <button onClick={() => {
            const text = backupCodes.join('\n');
            navigator.clipboard.writeText(text).catch(() => {});
            alert('Codes copied to clipboard');
          }}
            className="px-3.5 py-2 rounded-lg border-[1.5px] border-brand-primary bg-white text-brand-primary cursor-pointer font-semibold text-[0.85rem] mr-2">
            📋 Copy to clipboard
          </button>
          <button onClick={loadStatus}
            className="px-3.5 py-2 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.85rem]">
            I've saved them — continue
          </button>
        </div>
      )}
    </div>
  );
}